import { useState } from 'react';
import { Pencil, Plus, Trash2, UserCheck } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCompanyAdministrators,
  useCreateCompanyAdministrator,
  useDeleteCompanyAdministrator,
  useUpdateCompanyAdministrator,
} from '../../hooks/useCompanies';
import { usePeople } from '../../hooks/usePeople';
import { updatePersonContact } from '../../lib/peopleApi';
import type { CompanyAdministrator, CompanyAdministratorRole } from '../../lib/companiesApi';
import { ApiError } from '../../lib/api';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { useConfirm } from '../ui/ConfirmProvider';
import { useI18n } from '../../contexts/I18nContext';

// Administrators / sole associates card (CLE-ADMIN) — mounted on the company
// detail page (/settings/companies/:id). Manages the mandate history: each
// mandate links a Person from the team directory to the firm with a role
// (ADMINISTRATOR / ASOCIAT_UNIC) and a date range (open end = active mandate).
// Editing a mandate can also re-link it to another person and rename the
// linked person (the rename goes through PATCH /api/people/:id/contact, so it
// propagates system-wide and needs people.canEdit).

interface MandateFormState {
  personId: string;
  /** Editable display name of the linked person (edit mode only). */
  personFullName: string;
  role: CompanyAdministratorRole;
  startDate: string;
  endDate: string;
}

const EMPTY_FORM: MandateFormState = {
  personId: '',
  personFullName: '',
  role: 'ADMINISTRATOR',
  startDate: '',
  endDate: '',
};

function formatMonth(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('ro-RO', {
    month: 'short',
    year: 'numeric',
  });
}

export function AdministratorsCard({ companyId }: { companyId: string }) {
  const { t } = useI18n();
  const { data: mandates, isLoading, isError } = useCompanyAdministrators(companyId);
  const { data: people } = usePeople({ active: 'true' });
  const qc = useQueryClient();
  const createMandate = useCreateCompanyAdministrator(companyId);
  const updateMandate = useUpdateCompanyAdministrator(companyId);
  const deleteMandate = useDeleteCompanyAdministrator(companyId);
  const confirm = useConfirm();

  // 'new' = add form open; a mandate id = inline edit of that mandate.
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [form, setForm] = useState<MandateFormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const roleOptions: Array<{ value: CompanyAdministratorRole; label: string }> = [
    { value: 'ADMINISTRATOR', label: t('companies.admins.role.ADMINISTRATOR') },
    { value: 'ASOCIAT_UNIC', label: t('companies.admins.role.ASOCIAT_UNIC') },
  ];

  function openAdd() {
    setForm(EMPTY_FORM);
    setError(null);
    setEditing('new');
  }

  function openEdit(m: CompanyAdministrator) {
    setForm({
      personId: m.personId,
      personFullName: m.person.fullName,
      role: m.role,
      startDate: m.startDate.slice(0, 10),
      endDate: m.endDate ? m.endDate.slice(0, 10) : '',
    });
    setError(null);
    setEditing(m.id);
  }

  function failureMessage(e: unknown): string {
    if (e instanceof ApiError && e.code === 'MANDATE_OVERLAP') {
      return t('companies.admins.overlapError');
    }
    return e instanceof Error ? e.message : t('companies.error.generic');
  }

  async function handleSubmit() {
    if (!form.personId) {
      setError(t('companies.admins.personRequired'));
      return;
    }
    if (!form.startDate) {
      setError(t('companies.admins.startRequired'));
      return;
    }
    setError(null);
    try {
      if (editing === 'new') {
        await createMandate.mutateAsync({
          personId: form.personId,
          role: form.role,
          startDate: form.startDate,
          endDate: form.endDate || null,
        });
      } else if (editing) {
        // 1) Rename the linked person when the name was edited. Single write
        // path (the people endpoint) so the rename propagates system-wide;
        // needs people.canEdit — a 403 surfaces via the generic error message.
        const selectedName =
          (people ?? []).find((p) => p.id === form.personId)?.fullName ??
          mandates?.find((m) => m.personId === form.personId)?.person.fullName ??
          '';
        const nextName = form.personFullName.trim();
        if (nextName && nextName !== selectedName) {
          await updatePersonContact(form.personId, { fullName: nextName });
          void qc.invalidateQueries({ queryKey: ['people'] });
          void qc.invalidateQueries({ queryKey: ['person', form.personId] });
        }
        // 2) Then the mandate itself (possibly re-linked to another person).
        await updateMandate.mutateAsync({
          mandateId: editing,
          input: {
            personId: form.personId,
            role: form.role,
            startDate: form.startDate,
            endDate: form.endDate || null,
          },
        });
      }
      setEditing(null);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError(failureMessage(e));
    }
  }

  async function handleEndMandate(m: CompanyAdministrator) {
    setError(null);
    try {
      await updateMandate.mutateAsync({
        mandateId: m.id,
        input: { endDate: new Date().toISOString().slice(0, 10) },
      });
    } catch (e) {
      setError(failureMessage(e));
    }
  }

  async function handleDelete(m: CompanyAdministrator) {
    if (
      !(await confirm({
        message: t('companies.admins.deleteConfirm', { name: m.person.fullName }),
        danger: true,
      }))
    )
      return;
    setError(null);
    try {
      await deleteMandate.mutateAsync(m.id);
    } catch (e) {
      setError(failureMessage(e));
    }
  }

  const pending = createMandate.isPending || updateMandate.isPending || deleteMandate.isPending;

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{t('companies.admins.heading')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('companies.admins.hint')}</p>
        </div>
        {editing !== 'new' && (
          <button type="button" onClick={openAdd} className="ui-btn-secondary shrink-0">
            <Plus size={15} className="mr-1 inline" />
            {t('companies.admins.add')}
          </button>
        )}
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </p>
      )}

      {editing === 'new' && (
        <MandateForm
          isNew
          form={form}
          setForm={setForm}
          people={(people ?? []).map((p) => ({ value: p.id, label: p.fullName }))}
          roleOptions={roleOptions}
          pending={pending}
          onSubmit={handleSubmit}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="mt-4">
        {isLoading && <p className="text-sm text-gray-500">{t('companies.loading')}</p>}
        {isError && <p className="text-sm text-red-600">{t('companies.error.generic')}</p>}
        {mandates && mandates.length === 0 && (
          <p className="text-sm text-gray-500">{t('companies.admins.empty')}</p>
        )}
        {mandates && mandates.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {mandates.map((m) => {
              const isActive = m.endDate === null;
              const period = isActive
                ? t('companies.admins.periodActive', { start: formatMonth(m.startDate) })
                : `${formatMonth(m.startDate)} – ${formatMonth(m.endDate as string)}`;
              return (
                <li key={m.id} className="py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <UserCheck size={16} className="shrink-0 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{m.person.fullName}</span>
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                        m.role === 'ASOCIAT_UNIC'
                          ? 'bg-purple-500/15 text-purple-700'
                          : 'bg-violet-500/15 text-violet-700'
                      }`}
                    >
                      {t(`companies.admins.role.${m.role}`)}
                    </span>
                    <span className="text-xs text-gray-500">{period}</span>
                    <span className="ml-auto flex shrink-0 items-center gap-2">
                      {isActive && (
                        <button
                          type="button"
                          onClick={() => handleEndMandate(m)}
                          disabled={pending}
                          className="text-xs font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50"
                        >
                          {t('companies.admins.endMandate')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openEdit(m)}
                        disabled={pending}
                        aria-label={t('common.edit')}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(m)}
                        disabled={pending}
                        aria-label={t('common.delete')}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </div>
                  {editing === m.id && (
                    <MandateForm
                      isNew={false}
                      form={form}
                      setForm={setForm}
                      people={(people ?? []).map((p) => ({ value: p.id, label: p.fullName }))}
                      roleOptions={roleOptions}
                      pending={pending}
                      onSubmit={handleSubmit}
                      onCancel={() => setEditing(null)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

interface MandateFormProps {
  isNew: boolean;
  form: MandateFormState;
  setForm: (updater: (prev: MandateFormState) => MandateFormState) => void;
  people: Array<{ value: string; label: string }>;
  roleOptions: Array<{ value: CompanyAdministratorRole; label: string }>;
  pending: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

function MandateForm({
  isNew,
  form,
  setForm,
  people,
  roleOptions,
  pending,
  onSubmit,
  onCancel,
}: MandateFormProps) {
  const { t } = useI18n();
  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select
          label={t('companies.admins.person')}
          value={form.personId}
          onChange={(v) =>
            setForm((prev) => ({
              ...prev,
              personId: v,
              // Re-linking resets the editable name to the new person's name.
              personFullName: people.find((p) => p.value === v)?.label ?? '',
            }))
          }
          options={people}
          placeholder={t('companies.admins.personPlaceholder')}
        />
        {!isNew && (
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-gray-500">
              {t('companies.admins.personName')}
            </span>
            <input
              type="text"
              value={form.personFullName}
              onChange={(e) => setForm((prev) => ({ ...prev, personFullName: e.target.value }))}
              className="ui-input w-full"
            />
            <span className="mt-1 block text-[11px] text-gray-400">
              {t('companies.admins.personNameHint')}
            </span>
          </label>
        )}
        <Select
          label={t('companies.admins.role')}
          value={form.role}
          onChange={(v) => setForm((prev) => ({ ...prev, role: v as CompanyAdministratorRole }))}
          options={roleOptions}
        />
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-gray-500">
            {t('companies.admins.startDate')}
          </span>
          <DateField
            value={form.startDate}
            onChange={(v) => setForm((prev) => ({ ...prev, startDate: v }))}
            className="w-full"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-gray-500">
            {t('companies.admins.endDate')}
          </span>
          <DateField
            value={form.endDate}
            onChange={(v) => setForm((prev) => ({ ...prev, endDate: v }))}
            className="w-full"
          />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={pending}
          className="ui-btn-primary disabled:opacity-60"
        >
          {pending ? t('companies.saving') : t('common.save')}
        </button>
        <button type="button" onClick={onCancel} className="ui-btn-secondary">
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
